import { useState } from "react";

import {
  Button,
  classes,
  Modal,
  Table,
  type TableColumn,
  Text,
  toastSuccess,
  useDisclosure,
} from "@alpacahq/ui/old";

import {
  useGetOptionContractsBatch,
  useLiquidatePosition,
} from "~/v2/api/hooks";
import type { Asset } from "~/v2/api/rest";
import type { Account, Position } from "~/v2/api/rest/account";
import type { OptionContract } from "~/v2/api/rest/data";
import { SymbolLink } from "~/v2/components/SymbolLink";
import { ToggleButtonGroup } from "~/v2/components/ToggleButtonGroup";
import {
  getPrettyCash,
  getPrettyPercent,
  getPrettyQty,
  getProfitLossColor,
} from "~/v2/utils/formatting";
import { Radio } from "../entityOnboarding/Radio";
import { Orderbook } from "./Orderbook";
import { Orders } from "./Orders";

type View = "Position" | "Orders" | "Orderbook";

const DEFAULT_VIEW_OPTIONS: View[] = ["Position", "Orders"];

interface PortfolioProps {
  product: "live" | "paper";
  account: Account;
  asset: Asset;
  positions: {
    list: Position[];
    refetch: () => void;
  };
}

export const Portfolio = ({
  product,
  account,
  asset,
  positions,
}: PortfolioProps) => {
  const disclosure = useDisclosure();

  const [view, setView] = useState<View>("Position");
  const [symbol, setSymbol] = useState<string>("");
  const [cancelOrders, setCancelOrders] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const optionPositions = positions.list.filter(
    ({ symbol, asset_class }) =>
      asset_class === "us_option" &&
      symbol.substring(0, symbol.search(/\d/)) === asset?.symbol,
  );

  const { data: optionContracts } = useGetOptionContractsBatch({
    symbols: optionPositions.map(({ symbol }) => symbol),
    options: { enabled: !!asset },
  });

  const columns: TableColumn<Position>[] = [
    {
      name: "Symbol",
      key: "symbol",
      cellRenderer: ({ symbol }) => <SymbolLink symbol={symbol} />,
    },
    {
      name: "Qty",
      key: "qty",
      alignRight: true,
      cellRenderer: ({ qty }) => <Text>{getPrettyQty(qty)}</Text>,
    },
    {
      name: "Market Value",
      key: "market_value",
      alignRight: true,
      cellRenderer: ({ market_value }) => (
        <Text>{getPrettyCash(market_value)}</Text>
      ),
    },
    {
      name: "Avg Entry",
      key: "avg_entry_price",
      alignRight: true,
      cellRenderer: ({ avg_entry_price }) => (
        <Text>{getPrettyCash(avg_entry_price, { forcePrecision: 4 })}</Text>
      ),
    },
    {
      name: "Cost Basis",
      key: "cost_basis",
      alignRight: true,
      cellRenderer: ({ cost_basis }) => (
        <Text>{getPrettyCash(cost_basis, { forcePrecision: 4 })}</Text>
      ),
    },
    {
      name: "Today's P/L (%)",
      key: "unrealized_intraday_plpc",
      alignRight: true,
      cellRenderer: ({ unrealized_intraday_plpc }) => (
        <Text className={classes(getProfitLossColor(unrealized_intraday_plpc))}>
          {getPrettyPercent(unrealized_intraday_plpc, false, true)}
        </Text>
      ),
    },
    {
      name: "Today's P/L ($)",
      key: "unrealized_intraday_pl",
      alignRight: true,
      cellRenderer: ({ unrealized_intraday_pl }) => (
        <Text className={classes(getProfitLossColor(unrealized_intraday_pl))}>
          {getPrettyCash(unrealized_intraday_pl)}
        </Text>
      ),
    },
    {
      name: "Total P/L (%)",
      key: "unrealized_plpc",
      alignRight: true,
      cellRenderer: ({ unrealized_plpc }) => (
        <Text className={classes(getProfitLossColor(unrealized_plpc))}>
          {getPrettyPercent(unrealized_plpc, false, true)}
        </Text>
      ),
    },
    {
      name: "Total P/L ($)",
      key: "unrealized_pl",
      alignRight: true,
      cellRenderer: ({ unrealized_pl }) => {
        const num = Number(unrealized_pl || 0);

        return (
          <Text className={classes(getProfitLossColor(unrealized_pl))}>
            {getPrettyCash(num, { withPrefix: true })}
          </Text>
        );
      },
    },
    {
      name: "Action",
      key: "action",
      cellRenderer: (position: Position) => (
        <Button onClick={() => onOpen(position)} variant="red">
          Liquidate
        </Button>
      ),
    },
  ];

  const onChange = (isChecked: boolean) => setCancelOrders(isChecked);

  const onOpen = ({ symbol }: Position) => {
    setSymbol(symbol);
    document.getElementById("root")?.classList.add("blur");
    disclosure.onOpen();
  };

  const onClose = () => {
    setSymbol("");
    document.getElementById("root")?.classList.remove("blur");
    disclosure.onClose();
  };

  const mutation = useLiquidatePosition({
    onSuccess: () => {
      setIsLoading(false);
      toastSuccess("Position liquidated");
      positions.refetch();
    },
    onSettled: () => {
      onClose();
    },
  });

  const optionsColumns: TableColumn<OptionContract & Position>[] = [
    {
      name: "Symbol",
      key: "symbol",
      cellRenderer: ({ symbol }) => {
        return <SymbolLink symbol={symbol} />;
      },
    },
    {
      name: "Qty",
      key: "qty",
      alignRight: true,
      cellRenderer: ({ qty }) => <Text>{getPrettyQty(qty)}</Text>,
    },
    {
      name: "Market Value",
      key: "market_value",
      alignRight: true,
      cellRenderer: ({ market_value }) => (
        <Text>{getPrettyCash(market_value)}</Text>
      ),
    },
    {
      name: "Avg Entry",
      key: "avg_entry_price",
      alignRight: true,
      cellRenderer: ({ avg_entry_price }) => (
        <Text>{getPrettyCash(avg_entry_price, { forcePrecision: 4 })}</Text>
      ),
    },
    {
      name: "Cost Basis",
      key: "cost_basis",
      alignRight: true,
      cellRenderer: ({ cost_basis }) => (
        <Text>{getPrettyCash(cost_basis, { forcePrecision: 4 })}</Text>
      ),
    },
    {
      name: "Today's P/L (%)",
      key: "unrealized_intraday_plpc",
      alignRight: true,
      cellRenderer: ({ unrealized_intraday_plpc }) => (
        <Text className={classes(getProfitLossColor(unrealized_intraday_plpc))}>
          {getPrettyPercent(unrealized_intraday_plpc, false, true)}
        </Text>
      ),
    },
    {
      name: "Today's P/L ($)",
      key: "unrealized_intraday_pl",
      alignRight: true,
      cellRenderer: ({ unrealized_intraday_pl }) => (
        <Text className={classes(getProfitLossColor(unrealized_intraday_pl))}>
          {getPrettyCash(unrealized_intraday_pl)}
        </Text>
      ),
    },
    {
      name: "Total P/L (%)",
      key: "unrealized_plpc",
      alignRight: true,
      cellRenderer: ({ unrealized_plpc }) => (
        <Text className={classes(getProfitLossColor(unrealized_plpc))}>
          {getPrettyPercent(unrealized_plpc, false, true)}
        </Text>
      ),
    },
    {
      name: "Total P/L ($)",
      key: "unrealized_pl",
      alignRight: true,
      cellRenderer: ({ unrealized_pl }) => {
        const num = Number(unrealized_pl || 0);

        return (
          <Text className={classes(getProfitLossColor(unrealized_pl))}>
            {getPrettyCash(num, { withPrefix: true })}
          </Text>
        );
      },
    },
    {
      name: "Type",
      key: "type",
    },
    {
      name: "Style",
      key: "style",
    },
    {
      name: "Strike Price",
      key: "strike_price",
      alignRight: true,
      cellRenderer: ({ strike_price }) => getPrettyCash(strike_price),
    },
    {
      name: "Size",
      key: "size",
      alignRight: true,
      cellRenderer: ({ size }) =>
        Number(size).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 9,
        }),
    },
    {
      name: "Open Interest",
      key: "open_interest",
      alignRight: true,
      cellRenderer: ({ open_interest }) =>
        Number(open_interest).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 9,
        }),
    },
    {
      name: "Close Price",
      key: "close_price",
      alignRight: true,
      cellRenderer: ({ close_price }) =>
        getPrettyCash(close_price ?? undefined),
    },
    {
      name: "Status",
      key: "status",
    },
    {
      name: "Tradable",
      key: "tradable",
      cellRenderer: ({ tradable }) => (tradable ? "Yes" : "No"),
    },
    {
      name: "Expiration Date",
      key: "expiration_date",
      alignRight: true,
    },
    {
      name: "Open Interest Date",
      key: "open_interest_date",
      alignRight: true,
    },
    {
      name: "Close Price Date",
      key: "close_price_date",
      alignRight: true,
    },
  ];

  const sanitize = (value: string = "") =>
    value.replaceAll(/[^a-zA-Z0-9]/g, "");

  const positionsData =
    positions.list.filter(
      ({ symbol }) => sanitize(symbol) === sanitize(asset?.symbol),
    ) ?? [];

  const contractsData: (OptionContract & Position)[] =
    (optionContracts ?? [])
      .filter((contract): contract is OptionContract => !!contract)
      .filter((contract) =>
        optionPositions.some(
          (position) =>
            !!position?.symbol &&
            !!contract?.symbol &&
            position.symbol === contract.symbol,
        ),
      )
      .map((contract) => ({
        ...contract,
        ...(optionPositions.find(
          (position) => position.symbol === contract.symbol,
        ) as Position),
      })) || [];

  const onClick = () => {
    setIsLoading(true);
    if (symbol) {
      mutation.mutate(
        {
          symbol,
          cancelOrders,
          accountID: account?.id || "",
          product,
        },
        {
          onSettled: () => onClose(),
        },
      );
    }
  };

  const viewOptions: View[] =
    asset.class === "crypto"
      ? [...DEFAULT_VIEW_OPTIONS, "Orderbook"]
      : DEFAULT_VIEW_OPTIONS;

  const onViewOptionClicked = (option: string) => {
    setView(option as View);
  };

  return (
    <>
      <div className="w-full space-y-8">
        <ToggleButtonGroup
          options={viewOptions}
          selectedOption={view}
          onOptionClick={onViewOptionClicked}
        />
        {
          // Only show the positions table if the view is "Positions"
          view === "Position" && (
            <>
              <div>
                {!!account?.options_approved_level && (
                  <h4>Underlying position:</h4>
                )}
                <Table
                  maxHeight={450}
                  columns={columns}
                  uniqueKey="tradePortfolio:stock-positions"
                  rows={positionsData}
                  emptyMessage="No positions found."
                />
              </div>

              {!!account?.options_approved_level &&
                asset.class !== "crypto" && (
                  <div>
                    <h4>Contract(s) for this symbol:</h4>

                    <Table
                      maxHeight={450}
                      columns={optionsColumns}
                      paginationEnabled
                      pageSize={10}
                      uniqueKey="tradePortfolio:option-positions"
                      rows={contractsData}
                      emptyMessage="No contracts found."
                    />
                  </div>
                )}
            </>
          )
        }
        {view === "Orders" && <Orders symbol={asset?.symbol || ""} />}
        {view === "Orderbook" && <Orderbook asset={asset} />}
      </div>
      <Modal
        title="Warning"
        isOpen={disclosure.isOpen}
        onClose={onClose}
        controls={
          <div className="flex items-center justify-between gap-4">
            <Button variant="alternate" onClick={onClose} disabled={true}>
              Cancel
            </Button>
            <Button onClick={onClick} disabled={isLoading}>
              Confirm
            </Button>
          </div>
        }
      >
        <Text>
          Are you sure you want to liquidate your <b>{symbol}</b> positions?
        </Text>
        <br />
        <Radio
          name="cancel_all_open_orders"
          label="Cancel all open orders for this symbol?"
          isChecked={cancelOrders}
          onChange={onChange}
        />
      </Modal>
    </>
  );
};
